import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Alert,
  Link as MuiLink,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useRegisterMutation, useValidateRefIdMutation } from "api/endpoints/auth";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ROUTE_PATH } from "router/router-constant/router.constant";

const validationSchema = Yup.object({
  fullName: Yup.string()
    .min(2, "نام باید حداقل 2 کاراکتر باشد")
    .required("نام الزامی است"),
  phoneNumber: Yup.string()
    .matches(/^09\d{9}$/, "شماره تلفن باید با 09 شروع شده و 11 رقم باشد")
    .required("شماره تلفن الزامی است"),
});

interface FormValues {
  fullName: string;
  phoneNumber: string;
}

export const AuthRegisterComponent = () => {
  const [searchParams] = useSearchParams();
  const refId = searchParams.get("refId");
  
  const [register, { isLoading, error }] = useRegisterMutation();
  const [validateRefId, { isLoading: isValidating }] = useValidateRefIdMutation();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [refError, setRefError] = useState<string>("");
  const [referrerName, setReferrerName] = useState<string>("");
  const [isRefValid, setIsRefValid] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkRefId = async () => {
      if (!refId) {
        setRefError("کد معرف الزامی است. این صفحه فقط از طریق لینک دعوت قابل دسترسی است.");
        return;
      }

      try {
        const result = await validateRefId({
          payload: { refId },
        }).unwrap();

        if (result.valid) {
          setIsRefValid(true);
          setReferrerName(result.referrerName || "");
          setRefError("");
        } else {
          setRefError("کد معرف نامعتبر است");
        }
      } catch (err) {
        setRefError("کد معرف نامعتبر است");
      }
    };

    checkRefId();
  }, [refId, validateRefId]);

  const handleSubmit = async (values: FormValues) => {
    if (!refId || !isRefValid) {
      return;
    }

    try {
      const result = await register({
        payload: {
          ...values,
          refId,
        },
      }).unwrap();

      setSuccessMessage(result.message);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(ROUTE_PATH.auth.login.path);
      }, 3000);
    } catch (err) {
      console.error("Registration failed:", err);
    }
  };

  if (isValidating) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="grey.100"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (refError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="grey.100"
        p={2}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: "100%" }}>
          <Alert severity="error">
            {refError}
          </Alert>
          <Box textAlign="center" mt={2}>
            <Typography variant="body2">
              <MuiLink component={Link} to={ROUTE_PATH.auth.login.path}>
                بازگشت به صفحه ورود
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="grey.100"
      p={2}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: "100%" }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" gutterBottom>
            ثبت نام در Velora
          </Typography>
          <Typography variant="body2" color="text.secondary">
            برای استفاده از سرویس CRM ثبت نام کنید
          </Typography>
          {referrerName && (
            <Alert severity="info" sx={{ mt: 2 }}>
              شما توسط {referrerName} دعوت شده‌اید
            </Alert>
          )}
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {"data" in error && (error.data as any)?.message
              ? (error.data as any).message
              : "خطا در ثبت نام"}
          </Alert>
        )}

        <Formik
          initialValues={{
            fullName: "",
            phoneNumber: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              <TextField
                fullWidth
                name="fullName"
                label="نام و نام خانوادگی"
                value={values.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.fullName && Boolean(errors.fullName)}
                helperText={touched.fullName && errors.fullName}
                margin="normal"
                dir="rtl"
              />

              <TextField
                fullWidth
                name="phoneNumber"
                label="شماره تلفن (واتساپ)"
                value={values.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                helperText={
                  touched.phoneNumber && errors.phoneNumber
                    ? errors.phoneNumber
                    : "لطفاً شماره‌ای که واتساپ دارد وارد کنید"
                }
                margin="normal"
                placeholder="09123456789"
                dir="ltr"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading || Boolean(successMessage)}
                sx={{ mt: 3, mb: 2 }}
              >
                {isLoading ? "در حال ثبت نام..." : "ثبت نام"}
              </Button>

              <Box textAlign="center">
                <Typography variant="body2">
                  قبلاً ثبت نام کرده‌اید؟{" "}
                  <MuiLink component={Link} to={ROUTE_PATH.auth.login.path}>
                    وارد شوید
                  </MuiLink>
                </Typography>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};
